import React, {
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import {
  ContentCopyOutlined,
  EditOutlined,
  LocalShippingOutlined,
  OpenInNewOutlined,
  RefreshOutlined,
} from '@mui/icons-material';

import {
  DataGrid,
} from '@mui/x-data-grid';

import {
  tokens,
} from '../../../theme';

import {
  useCreatePublicOrderStatusLinkMutation,
  useGetAdminOrdersQuery,
  useUpdateOrderFulfillmentMutation,
} from '../../../features/orders/ordersApiSlice';


const PAYMENT_STATUSES = [
  'pending',
  'paid',
  'payment_failed',
  'canceled',
  'fulfilled',
];


const FULFILLMENT_STATUSES = [
  'unfulfilled',
  'processing',
  'shipped',
  'delivered',
  'canceled',
];


const formatCurrency =
  (
    cents,
    currency = 'usd'
  ) => {
    return new Intl
      .NumberFormat(
        'en-US',
        {
          style:
            'currency',

          currency:
            String(
              currency ||
              'usd'
            ).toUpperCase(),
        }
      )
      .format(
        Number(
          cents || 0
        ) / 100
      );
  };


const formatDate =
  (value) => {
    if (!value) {
      return '—';
    }

    const date =
      new Date(
        value
      );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '—';
    }

    return date
      .toLocaleString();
  };


const getPaymentChipColor =
  (status) => {
    switch (status) {
      case 'paid':
        return 'success';

      case 'pending':
        return 'warning';

      case 'payment_failed':
        return 'error';

      case 'canceled':
        return 'default';

      case 'fulfilled':
        return 'success';

      default:
        return 'default';
    }
  };


const getFulfillmentChipColor =
  (status) => {
    switch (status) {
      case 'processing':
        return 'info';

      case 'shipped':
        return 'primary';

      case 'delivered':
        return 'success';

      case 'canceled':
        return 'error';

      default:
        return 'default';
    }
  };


const OrderManagement =
  () => {
    const theme =
      useTheme();

    const colors =
      tokens(
        theme.palette.mode
      );


    const {
      data,
      isLoading,
      isFetching,
      isError,
      error,
      refetch,
    } =
      useGetAdminOrdersQuery();


    const [
      updateFulfillment,
      {
        isLoading:
          isSaving,
      },
    ] =
      useUpdateOrderFulfillmentMutation();


    const [
      createPublicLink,
      {
        isLoading:
          isCreatingLink,
      },
    ] =
      useCreatePublicOrderStatusLinkMutation();


    const [
      search,
      setSearch,
    ] =
      useState('');


    const [
      paymentFilter,
      setPaymentFilter,
    ] =
      useState('all');


    const [
      fulfillmentFilter,
      setFulfillmentFilter,
    ] =
      useState('all');


    const [
      selectedOrder,
      setSelectedOrder,
    ] =
      useState(null);


    const [
      form,
      setForm,
    ] =
      useState({
        status:
          'unfulfilled',

        carrier:
          '',

        trackingNumber:
          '',

        trackingUrl:
          '',

        internalNotes:
          '',
      });


    const [
      snackbar,
      setSnackbar,
    ] =
      useState({
        open:
          false,

        message:
          '',

        severity:
          'success',
      });


    const showMessage =
      (
        message,
        severity =
          'success'
      ) => {
        setSnackbar({
          open:
            true,

          message,

          severity,
        });
      };


    const rows =
      useMemo(
        () => {
          return (
            data?.orders ||
            []
          ).map(
            (order) => {
              const itemsSummary =
                (
                  order.items ||
                  []
                )
                  .map(
                    (item) =>
                      `${item.quantity}× ${item.name}`
                  )
                  .join(', ');

              return {
                ...order,

                id:
                  order._id,

                itemsSummary,

                paymentStatus:
                  order.status,

                fulfillmentStatus:
                  order
                    .fulfillment
                    ?.status ||
                  (
                    order.status ===
                    'fulfilled'
                      ? 'delivered'
                      : 'unfulfilled'
                  ),

                carrier:
                  order
                    .fulfillment
                    ?.carrier ||
                  '',

                trackingNumber:
                  order
                    .fulfillment
                    ?.trackingNumber ||
                  '',
              };
            }
          );
        },
        [
          data,
        ]
      );


    const filteredRows =
      useMemo(
        () => {
          const needle =
            search
              .trim()
              .toLowerCase();

          return rows.filter(
            (row) => {
              const matchesSearch =
                !needle ||
                [
                  row._id,
                  row.customerName,
                  row.email,
                  row.organizationName,
                  row.itemsSummary,
                  row.carrier,
                  row.trackingNumber,
                ]
                  .filter(
                    Boolean
                  )
                  .some(
                    (value) =>
                      String(
                        value
                      )
                        .toLowerCase()
                        .includes(
                          needle
                        )
                  );


              const matchesPayment =
                paymentFilter ===
                  'all' ||
                row.paymentStatus ===
                  paymentFilter;


              const matchesFulfillment =
                fulfillmentFilter ===
                  'all' ||
                row.fulfillmentStatus ===
                  fulfillmentFilter;


              return (
                matchesSearch &&
                matchesPayment &&
                matchesFulfillment
              );
            }
          );
        },
        [
          rows,
          search,
          paymentFilter,
          fulfillmentFilter,
        ]
      );


    const handleOpenOrder =
      (order) => {
        setSelectedOrder(
          order
        );

        setForm({
          status:
            order
              .fulfillment
              ?.status ||
            (
              order.status ===
              'fulfilled'
                ? 'delivered'
                : 'unfulfilled'
            ),

          carrier:
            order
              .fulfillment
              ?.carrier ||
            '',

          trackingNumber:
            order
              .fulfillment
              ?.trackingNumber ||
            '',

          trackingUrl:
            order
              .fulfillment
              ?.trackingUrl ||
            '',

          internalNotes:
            order
              .fulfillment
              ?.internalNotes ||
            '',
        });
      };


    const handleClose =
      () => {
        if (
          isSaving
        ) {
          return;
        }

        setSelectedOrder(
          null
        );
      };


    const setField =
      (
        field,
        value
      ) => {
        setForm(
          (previous) => ({
            ...previous,

            [field]:
              value,
          })
        );
      };


    const handleSave =
      async () => {
        if (
          !selectedOrder
        ) {
          return;
        }

        try {
          await updateFulfillment({
            orderId:
              selectedOrder._id,

            ...form,
          }).unwrap();

          showMessage(
            'Fulfillment updated.'
          );

          setSelectedOrder(
            null
          );
        } catch (error) {
          showMessage(
            error?.data
              ?.message ||
            'Unable to update fulfillment.',
            'error'
          );
        }
      };


    const handleCopyPublicLink =
      async () => {
        if (
          !selectedOrder
        ) {
          return;
        }

        try {
          const result =
            await createPublicLink(
              selectedOrder._id
            ).unwrap();

          await navigator
            .clipboard
            .writeText(
              result.url
            );

          showMessage(
            'Customer order-status link copied.'
          );
        } catch (error) {
          showMessage(
            error?.data
              ?.message ||
            'Unable to create status link.',
            'error'
          );
        }
      };


    const columns =
      useMemo(
        () => [
          {
            field:
              'createdAt',

            headerName:
              'Ordered',

            width:
              175,

            renderCell:
              (params) =>
                formatDate(
                  params.value
                ),
          },

          {
            field:
              '_id',

            headerName:
              'Order',

            width:
              115,

            renderCell:
              (params) => (
                <Tooltip
                  title={
                    params.value
                  }
                >
                  <Typography
                    variant="body2"
                    fontWeight={
                      700
                    }
                  >
                    {String(
                      params.value
                    )
                      .slice(-8)
                      .toUpperCase()}
                  </Typography>
                </Tooltip>
              ),
          },

          {
            field:
              'customerName',

            headerName:
              'Customer',

            minWidth:
              170,

            flex:
              1,
          },

          {
            field:
              'email',

            headerName:
              'Email',

            minWidth:
              210,

            flex:
              1.2,
          },

          {
            field:
              'itemsSummary',

            headerName:
              'Items',

            minWidth:
              220,

            flex:
              1.3,
          },

          {
            field:
              'amountTotalCents',

            headerName:
              'Total',

            width:
              110,

            renderCell:
              (params) =>
                formatCurrency(
                  params.value,
                  params.row
                    .currency
                ),
          },

          {
            field:
              'paymentStatus',

            headerName:
              'Payment',

            width:
              135,

            renderCell:
              (params) => (
                <Chip
                  size="small"
                  label={
                    params.value
                  }
                  color={
                    getPaymentChipColor(
                      params.value
                    )
                  }
                />
              ),
          },

          {
            field:
              'fulfillmentStatus',

            headerName:
              'Fulfillment',

            width:
              140,

            renderCell:
              (params) => (
                <Chip
                  size="small"
                  label={
                    params.value
                  }
                  color={
                    getFulfillmentChipColor(
                      params.value
                    )
                  }
                />
              ),
          },

          {
            field:
              'carrier',

            headerName:
              'Carrier',

            width:
              100,
          },

          {
            field:
              'trackingNumber',

            headerName:
              'Tracking',

            minWidth:
              160,

            flex:
              0.8,

            renderCell:
              (params) => {
                if (
                  !params.value
                ) {
                  return '—';
                }

                const url =
                  params.row
                    .fulfillment
                    ?.trackingUrl;

                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={
                      0.5
                    }
                  >
                    <Typography
                      variant="body2"
                    >
                      {params.value}
                    </Typography>

                    {url && (
                      <Tooltip
                        title="Open tracking"
                      >
                        <IconButton
                          size="small"
                          component="a"
                          href={
                            url
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewOutlined
                            fontSize="inherit"
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                );
              },
          },

          {
            field:
              'actions',

            headerName:
              '',

            width:
              75,

            sortable:
              false,

            filterable:
              false,

            renderCell:
              (params) => (
                <Tooltip
                  title="Manage fulfillment"
                >
                  <IconButton
                    onClick={
                      () =>
                        handleOpenOrder(
                          params.row
                        )
                    }
                  >
                    <EditOutlined />
                  </IconButton>
                </Tooltip>
              ),
          },
        ],
        []
      );


    return (
      <Box
        m="20px"
      >
        <Stack
          direction={{
            xs:
              'column',

            md:
              'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs:
              'flex-start',

            md:
              'center',
          }}
          spacing={
            2
          }
          mb={
            3
          }
        >
          <Box>
            <Typography
              variant="h2"
              fontWeight={
                800
              }
            >
              Orders
            </Typography>

            <Typography
              mt={
                1
              }
              color="text.secondary"
            >
              Manage USFuel orders,
              fulfillment, shipping,
              and tracking.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={
              isFetching
                ? (
                  <CircularProgress
                    size={
                      16
                    }
                  />
                )
                : (
                  <RefreshOutlined />
                )
            }
            disabled={
              isFetching
            }
            onClick={
              refetch
            }
          >
            Refresh
          </Button>
        </Stack>


        {/* FILTERS */}

        <Stack
          direction={{
            xs:
              'column',

            lg:
              'row',
          }}
          spacing={
            2
          }
          mb={
            2
          }
        >
          <TextField
            label="Search orders"
            placeholder="Customer, email, order, item, tracking..."
            size="small"
            value={
              search
            }
            onChange={
              (event) =>
                setSearch(
                  event.target.value
                )
            }
            sx={{
              minWidth:
                320,
            }}
          />

          <TextField
            select
            size="small"
            label="Payment"
            value={
              paymentFilter
            }
            onChange={
              (event) =>
                setPaymentFilter(
                  event.target.value
                )
            }
            sx={{
              minWidth:
                170,
            }}
          >
            <MenuItem
              value="all"
            >
              All payments
            </MenuItem>

            {PAYMENT_STATUSES.map(
              (status) => (
                <MenuItem
                  key={
                    status
                  }
                  value={
                    status
                  }
                >
                  {status}
                </MenuItem>
              )
            )}
          </TextField>

          <TextField
            select
            size="small"
            label="Fulfillment"
            value={
              fulfillmentFilter
            }
            onChange={
              (event) =>
                setFulfillmentFilter(
                  event.target.value
                )
            }
            sx={{
              minWidth:
                180,
            }}
          >
            <MenuItem
              value="all"
            >
              All fulfillment
            </MenuItem>

            {FULFILLMENT_STATUSES.map(
              (status) => (
                <MenuItem
                  key={
                    status
                  }
                  value={
                    status
                  }
                >
                  {status}
                </MenuItem>
              )
            )}
          </TextField>

          <Button
            onClick={
              () => {
                setSearch(
                  ''
                );

                setPaymentFilter(
                  'all'
                );

                setFulfillmentFilter(
                  'all'
                );
              }
            }
          >
            Clear Filters
          </Button>
        </Stack>


        {isError && (
          <Alert
            severity="error"
            sx={{
              mb:
                2,
            }}
          >
            {error?.data
              ?.message ||
              'Unable to load orders.'}
          </Alert>
        )}


        <Box
          sx={{
            height:
              'calc(100vh - 280px)',

            minHeight:
              500,

            width:
              '100%',

            '& .MuiDataGrid-root': {
              borderColor:
                colors.grey[700],
            },
          }}
        >
          <DataGrid
            rows={
              filteredRows
            }
            columns={
              columns
            }
            loading={
              isLoading ||
              isFetching
            }
            disableRowSelectionOnClick
            pageSizeOptions={[
              25,
              50,
              100,
            ]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize:
                    25,

                  page:
                    0,
                },
              },
            }}
          />
        </Box>


        {/* FULFILLMENT EDITOR */}

        <Dialog
          open={
            Boolean(
              selectedOrder
            )
          }
          onClose={
            handleClose
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Stack
              direction="row"
              alignItems="center"
              spacing={
                1
              }
            >
              <LocalShippingOutlined />

              <span>
                Manage Fulfillment
              </span>
            </Stack>
          </DialogTitle>

          <DialogContent>
            {selectedOrder && (
              <Stack
                spacing={
                  2
                }
                mt={
                  1
                }
              >
                <Box>
                  <Typography
                    fontWeight={
                      800
                    }
                  >
                    {
                      selectedOrder
                        .customerName
                    }
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {
                      selectedOrder
                        .email
                    }
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Order{' '}
                    {
                      selectedOrder
                        ._id
                    }
                  </Typography>
                </Box>


                <TextField
                  select
                  fullWidth
                  label="Fulfillment Status"
                  value={
                    form.status
                  }
                  onChange={
                    (event) =>
                      setField(
                        'status',
                        event.target.value
                      )
                  }
                >
                  {FULFILLMENT_STATUSES.map(
                    (status) => (
                      <MenuItem
                        key={
                          status
                        }
                        value={
                          status
                        }
                      >
                        {status}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  fullWidth
                  label="Carrier"
                  placeholder="UPS, FedEx, USPS..."
                  value={
                    form.carrier
                  }
                  onChange={
                    (event) =>
                      setField(
                        'carrier',
                        event.target.value
                      )
                  }
                />


                <TextField
                  fullWidth
                  label="Tracking Number"
                  value={
                    form.trackingNumber
                  }
                  onChange={
                    (event) =>
                      setField(
                        'trackingNumber',
                        event.target.value
                      )
                  }
                />


                <TextField
                  fullWidth
                  label="Tracking URL"
                  placeholder="https://..."
                  value={
                    form.trackingUrl
                  }
                  onChange={
                    (event) =>
                      setField(
                        'trackingUrl',
                        event.target.value
                      )
                  }
                />


                <TextField
                  fullWidth
                  multiline
                  minRows={
                    4
                  }
                  label="Internal Fulfillment Notes"
                  placeholder="Warehouse notes, shipping issues, device serial, etc."
                  value={
                    form.internalNotes
                  }
                  onChange={
                    (event) =>
                      setField(
                        'internalNotes',
                        event.target.value
                      )
                  }
                />


                <Button
                  variant="outlined"
                  startIcon={
                    <ContentCopyOutlined />
                  }
                  disabled={
                    isCreatingLink
                  }
                  onClick={
                    handleCopyPublicLink
                  }
                >
                  Copy Customer Status Link
                </Button>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={
                handleClose
              }
              disabled={
                isSaving
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={
                handleSave
              }
              disabled={
                isSaving
              }
            >
              {isSaving
                ? 'Saving...'
                : 'Save Fulfillment'}
            </Button>
          </DialogActions>
        </Dialog>


        <Snackbar
          open={
            snackbar.open
          }
          autoHideDuration={
            4000
          }
          onClose={
            () =>
              setSnackbar(
                (previous) => ({
                  ...previous,

                  open:
                    false,
                })
              )
          }
        >
          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };


export default OrderManagement;